import React from 'react';
import { useTranslation, useInView } from '../../../../hooks';
import { IMAGES } from '../../../../assets';
import { FadeInWrapper, SlideUpWrapper } from '../../../../components';
import styles from './Header.module.css';

/**
 * Header Component (Landing Feature)
 * 
 * Hero section component for the landing page featuring:
 * - Logo with interactive animations
 * - Main title and subtitle text
 * - Call-to-action download button
 * - Smooth animations with proper timing
 * - CSS Modules for scoped styling
 * - Responsive design
 * 
 * @component
 * @example
 * return (
 *   <Header />
 * )
 */
const Header = () => {
  const { t, language } = useTranslation();
  const [setRef, inView] = useInView();

  const handleDownloadClick = (e) => {
    e.preventDefault();
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      const yOffset = -80; // Offset to scroll above the section (80px above)
      const y = downloadSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <header className={styles.header} ref={setRef}>
      {/* Background Overlay */}
      <div className={styles.overlay} aria-hidden="true"></div>
      
      {/* Content Container */}
      <div className={styles.content}>
        {/* Logo */}
        <FadeInWrapper delay={0.2}>
          <img 
            src={IMAGES.branding.logoMain} 
            alt={language === 'ar' ? 'شعار بوابة الليطاني' : 'Litani Gateway Logo'} 
            className={`${styles.logo} ${styles.interactiveCard} ${inView ? styles.bounceWrapper : ''}`}
          />
        </FadeInWrapper>
        
        {/* Main Title */}
        <SlideUpWrapper delay={0.4}>
          <h1 className={styles.title}>
            {t.header.title}
          </h1>
        </SlideUpWrapper>
        
        {/* Platform Description */}
        <FadeInWrapper delay={0.5}>
          <div className={styles.platformDescription}>
            {language === 'ar' 
              ? 'المنصة الرقمية الرسمية لبلدة قاقعية الجسر' 
              : 'The Official Digital Platform of Qaqaaiyat AlJiser Village'
            }
          </div>
        </FadeInWrapper>
        
        {/* Subtitle */}
        <FadeInWrapper delay={0.6}>
          <p className={styles.subtitle}>
            {t.header.subtitle}
          </p>
        </FadeInWrapper>
        
        {/* Call to Action Button */}
        <SlideUpWrapper delay={0.8}>
          <div className={styles.ctaContainer}>
            <a 
              href="#download" 
              onClick={handleDownloadClick}
              className={`${styles.ctaButton} ${styles.btnEnhanced} ${styles.interactiveButton}`}
              aria-label={language === 'ar' ? 'تحميل التطبيق' : 'Download the app'}
            >
              {t.header.downloadBtn}
            </a>
          </div>
        </SlideUpWrapper>
      </div>
    </header>
  );
};

export default Header;